// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react'
import Box from '@mui/material/Box'
import AppButton from './AppButton'

/**
 * D-ESIGN-1 — the ONE signature pad (E-01 rule of two). It was hand-rolled inside CareRxPotQueue.tsx; the
 * POT §14 attestation (today's step in every mode, owner 2026-10-21) and every in-app signature draw here.
 *
 * Pointer events cover mouse, pen and touch; `touch-action: none` keeps a finger stroke from scrolling the
 * page. The backing store is scaled by devicePixelRatio so the stroke is sharp on a tablet, while the
 * exported PNG stays small. A stroke reports on pointer-up (or leaving the pad) — never per move.
 */
export interface SignaturePadProps {
  /** A PNG data URL of the drawing, or null once cleared. */
  onChange: (dataUrl: string | null) => void
  width?: number
  height?: number
  ariaLabel?: string
  disabled?: boolean
  /** A stored signature (PNG data URL) to show — e.g. a Plan of Treatment draft resumed with its §14
   *  signature. Redrawn when it changes from outside; the pad's own strokes are not redrawn. */
  value?: string | null
  /** Marks the pad as a missing required field (red border, aria-invalid). */
  missing?: boolean
}

export default function SignaturePad({ onChange, width = 360, height = 120, ariaLabel = 'Signature pad — draw your signature', disabled = false, value, missing = false }: SignaturePadProps) {
  const ref = useRef<HTMLCanvasElement | null>(null)
  const drawing = useRef(false)
  const dirty = useRef(false)
  const emitted = useRef<string | null | undefined>(undefined)   // what this pad last reported

  useEffect(() => {
    const c = ref.current
    const ctx = c?.getContext('2d')
    if (!c || !ctx) return
    const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1))
    c.width = Math.round(width * dpr)
    c.height = Math.round(height * dpr)
    ctx.scale(dpr, dpr)
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#1f2937'
  }, [width, height])

  // A stored signature handed in from outside (a resumed draft) is drawn; our own last stroke is not.
  useEffect(() => {
    const c = ref.current
    const ctx = c?.getContext('2d')
    if (!c || !ctx || value === undefined || value === emitted.current) return
    ctx.clearRect(0, 0, width, height)
    if (value) { const img = new Image(); img.onload = () => ctx.drawImage(img, 0, 0, width, height); img.src = value }
  }, [value, width, height])

  const point = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    // The canvas can be shrunk by CSS (max-width: 100%) — map back to drawing coordinates.
    return { x: (e.clientX - r.left) * (width / (r.width || width)), y: (e.clientY - r.top) * (height / (r.height || height)) }
  }
  const start = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (disabled) return
    const ctx = e.currentTarget.getContext('2d')
    if (!ctx) return
    drawing.current = true
    e.currentTarget.setPointerCapture?.(e.pointerId)
    const p = point(e)
    ctx.beginPath(); ctx.moveTo(p.x, p.y)
  }
  const move = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    const ctx = e.currentTarget.getContext('2d')
    if (!ctx) return
    const p = point(e)
    ctx.lineTo(p.x, p.y); ctx.stroke()
    dirty.current = true
  }
  const end = () => {
    if (!drawing.current) return
    drawing.current = false
    if (dirty.current && ref.current) { const url = ref.current.toDataURL('image/png'); emitted.current = url; onChange(url) }
  }
  const clear = () => {
    const c = ref.current
    c?.getContext('2d')?.clearRect(0, 0, c.width, c.height)
    dirty.current = false
    emitted.current = null
    onChange(null)
  }

  return (
    <Box>
      <Box component="canvas" ref={ref} role="img" aria-label={ariaLabel} data-testid="signature-pad" aria-invalid={missing || undefined}
        onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end} onPointerCancel={end}
        sx={{ display: 'block', width, height, maxWidth: '100%', border: missing ? '1px solid var(--color-danger)' : '1px dashed var(--color-border)', borderRadius: 2,
              background: '#fff', touchAction: 'none', cursor: disabled ? 'not-allowed' : 'crosshair', opacity: disabled ? 0.6 : 1 }} />
      <AppButton size="small" variant="text" onClick={clear} disabled={disabled} sx={{ mt: 0.5, px: 0, minWidth: 0 }}>
        Clear signature
      </AppButton>
    </Box>
  )
}
